import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { SalesTransactionProduct } from '@/api/models';
import { formatGel, getNodeTestIdProp } from '@/utils';
import { lineDiscountTag } from '../../transactionsMeta';

type TransactionDetailLineProps = {
    item: SalesTransactionProduct;
    /** The row's position in the cart, for its test ids. */
    index: number;
};

/**
 * One cart row of the transaction detail (render-only): the product name,
 * `quantity × list price`, and the row total. A discounted row (SEL-100) adds
 * a tag with its discount as entered (`−10%`, `−₾ 5.00`), the note when there
 * is one, and the list total struck through under the total after its own
 * discount. A row without a discount looks as it always has.
 */
export const TransactionDetailLine: React.FC<TransactionDetailLineProps> = ({
    item,
    index,
}) => {
    const { t } = useTranslation('pages');
    const { discount } = item;

    return (
        <div
            className="flex items-start justify-between gap-3 px-3 py-2"
            {...getNodeTestIdProp(`transaction-detail-line-${index}`)}
        >
            <div className="flex min-w-0 flex-col gap-0.5">
                <Typography className="truncate" type="body-sm">
                    {item.name}
                </Typography>
                <div className="flex flex-wrap items-center gap-2">
                    <Typography
                        className="tabular-nums"
                        color="muted"
                        type="body-xs"
                    >
                        {`${item.quantity} × ${formatGel(item.price)}`}
                    </Typography>
                    {discount ? (
                        <Chip color="warning" size="sm" variant="primary">
                            {lineDiscountTag(discount)}
                        </Chip>
                    ) : null}
                </div>
                {discount?.note ? (
                    <div className="flex items-center gap-1.5 text-muted">
                        <Icon className="size-3" icon="solar:notes-linear" />
                        <Typography color="muted" type="body-xs">
                            {t('transactions.modal.note', {
                                note: discount.note,
                            })}
                        </Typography>
                    </div>
                ) : null}
            </div>
            <div className="flex shrink-0 flex-col items-end tabular-nums">
                <Typography type="body-sm" weight="medium">
                    {formatGel(item.total)}
                </Typography>
                {discount ? (
                    <Typography
                        className="line-through"
                        color="muted"
                        type="body-xs"
                    >
                        {formatGel(item.listTotal)}
                    </Typography>
                ) : null}
            </div>
        </div>
    );
};
